import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { Link, useNavigate } from 'react-router-dom'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from 'dayjs'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const CreateOrganizationPage = () => {
  const [organizationData, setOrganizationData] = useState({
    name: '',
    address: '',
    phone: '',
    startDate: dayjs(),
    active: true,
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const navigate = useNavigate()

  const validate = () => {
    const newErrors = {}

    if (!organizationData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!organizationData.address.trim()) {
      newErrors.address = 'Address is required'
    }

    if (!organizationData.phone.trim()) {
      newErrors.phone = 'Phone is required'
    }

    if (!organizationData.startDate) {
      newErrors.startDate = 'Start Date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setOrganizationData({
      ...organizationData,
      [name]: type === 'checkbox' ? checked : value,
    })

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const handleDateChange = (newDate) => {
    setOrganizationData({
      ...organizationData,
      startDate: newDate,
    })

    if (errors.startDate) {
      setErrors({
        ...errors,
        startDate: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const formattedData = {
        ...organizationData,
        startDate: organizationData.startDate.toISOString(),
      }

      const { data, status } = await fetchWithAuth('organizations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      })

      if (status === 201) {
        navigate('/organizations')
      } else {
        if (data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else if (data.error === 'duplicate_key') {
          setError('An organization with this name already exists')
        } else {
          setError(data.message || 'Failed to create organization')
        }
      }
    } catch (error) {
      console.error('Error creating organization:', error)
      setError('Failed to create organization. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to="/organizations" edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Create New Organization
        </Typography>
      </Stack>

      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={organizationData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              label="Address"
              name="address"
              value={organizationData.address}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.address}
              helperText={errors.address}
            />

            <TextField
              label="Phone"
              name="phone"
              value={organizationData.phone}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.phone}
              helperText={errors.phone}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={organizationData.startDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                  />
                )}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate,
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel id="status-label" htmlFor="status">
                Status
              </InputLabel>
              <Select
                labelId="status-label"
                name="active"
                value={organizationData.active}
                onChange={handleChange}
                label="Status"
                inputProps={{ id: 'status' }}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/organizations"
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default CreateOrganizationPage
